import axios from 'axios';

const isLikelyUrlLike = (value: string) =>
  /^(https?:\/\/|\/|data:|blob:)/i.test(value);

const shouldAttemptUtf8Repair = (value: string) => {
  if (!value || isLikelyUrlLike(value)) {
    return false;
  }

  return /[\u00C2\u00C3\u00C5\u00C6\u00D0\u00D1]/.test(value);
};

const decodePossibleMojibake = (value: string): string => {
  if (!shouldAttemptUtf8Repair(value)) {
    return value;
  }

  try {
    const bytes = Uint8Array.from(Array.from(value, (char) => char.charCodeAt(0) & 0xff));
    const decoded = new TextDecoder('utf-8', { fatal: false }).decode(bytes).replace(/\u0000/g, '');

    if (!decoded || decoded === value) {
      return value;
    }

    if (/[\u0400-\u04FF\u4E00-\u9FFF]/.test(decoded)) {
      return decoded;
    }
  } catch (error) {
    return value;
  }

  return value;
};

const normalizeApiPayload = (value: any): any => {
  if (typeof value === 'string') {
    return decodePossibleMojibake(value);
  }

  if (Array.isArray(value)) {
    return value.map((item) => normalizeApiPayload(item));
  }

  if (value && typeof value === 'object') {
    return Object.entries(value).reduce<Record<string, any>>((accumulator, [key, nestedValue]) => {
      accumulator[decodePossibleMojibake(key)] = normalizeApiPayload(nestedValue);
      return accumulator;
    }, {});
  }

  return value;
};

const api = axios.create({
  baseURL: '',
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  withCredentials: true,
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
  timeout: 15000,
});

api.interceptors.response.use(
  (response) => {
    const contentType = String(response?.headers?.['content-type'] || '');
    if (response?.data && typeof response.data === 'object' && contentType.includes('application/json')) {
      response.data = normalizeApiPayload(response.data);
    }

    return response;
  },
  (error) => {
    const method = (error?.config?.method || '').toLowerCase();
    const url = String(error?.config?.url || '');

    // Ignore 404 only for read-only GET requests with optional data.
    if (error.response?.status === 404 && method === 'get' && !url.includes('/api/orders/status/')) {
      return Promise.resolve({ data: [] });
    }

    return Promise.reject(error);
  }
);

export default api;
